import React from 'react';
import DateTimePicker from './DateTimePicker';

/**
 * MultiStopInput - A reusable component for managing multiple pickup or delivery stops
 * Supports up to 5 stops per type with optional time windows
 */

const MAX_STOPS = 5;

const inputStyle = {
  width: '100%',
  padding: '0.75rem',
  backgroundColor: 'var(--surface-2)',
  border: '1px solid var(--accent-primary)',
  borderRadius: '5px',
  color: 'var(--text-primary)',
  fontSize: '1rem',
  boxSizing: 'border-box'
};

const timeInputStyle = {
  ...inputStyle,
  border: '1px solid var(--border-default)',
  padding: '0.5rem'
};

const buttonStyle = {
  padding: '0.5rem 1rem',
  backgroundColor: 'var(--btn-primary-bg)',
  color: 'var(--text-primary)',
  border: 'none',
  borderRadius: '5px',
  cursor: 'pointer',
  fontSize: '0.9rem',
  fontWeight: 'bold'
};

const removeButtonStyle = {
  ...buttonStyle,
  backgroundColor: 'var(--color-danger)',
  padding: '0.25rem 0.5rem',
  fontSize: '0.8rem'
};

function MultiStopInput({ 
  stops, 
  onChange, 
  stopType, // 'pickup' or 'delivery'
  required = false 
}) {
  const label = stopType === 'pickup' ? 'Pickup' : 'Delivery';
  const icon = stopType === 'pickup' ? '📦' : '🏁';

  const handleAddStop = () => {
    if (stops.length < MAX_STOPS) {
      onChange([
        ...stops,
        { 
          address: '', 
          time_window_start: '', 
          time_window_end: '',
          notes: ''
        }
      ]);
    }
  };

  const handleRemoveStop = (index) => {
    if (stops.length > 1) {
      const newStops = stops.filter((_, i) => i !== index);
      onChange(newStops);
    }
  };

  const handleStopChange = (index, field, value) => {
    const newStops = stops.map((stop, i) => {
      if (i === index) {
        return { ...stop, [field]: value };
      }
      return stop;
    });
    onChange(newStops);
  };

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '0.75rem'
      }}>
        <label style={{ 
          color: 'var(--accent-primary)', 
          fontWeight: 'bold',
          fontSize: '1.1rem'
        }}>
          {icon} {label} Stops {required && '*'}
        </label>
        {stops.length < MAX_STOPS && (
          <button
            type="button"
            onClick={handleAddStop}
            style={buttonStyle}
          >
            + Add {label}
          </button>
        )}
      </div>

      {stops.map((stop, index) => (
        <div 
          key={index}
          style={{
            backgroundColor: 'var(--bg-primary)',
            borderRadius: '8px',
            padding: '1rem',
            marginBottom: '0.75rem',
            border: '1px solid var(--border-default)'
          }}
        >
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '0.75rem'
          }}>
            <span style={{ 
              color: 'var(--accent-primary)', 
              fontWeight: 'bold',
              fontSize: '0.9rem'
            }}>
              {label} #{index + 1}
            </span>
            {stops.length > 1 && (
              <button
                type="button"
                onClick={() => handleRemoveStop(index)}
                style={removeButtonStyle}
              >
                Remove
              </button>
            )}
          </div>

          {/* Address */}
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ 
              display: 'block', 
              color: 'var(--text-secondary)', 
              marginBottom: '0.25rem',
              fontSize: '0.85rem'
            }}>
              Address {index === 0 && required && '*'}
            </label>
            <input
              type="text"
              value={stop.address}
              onChange={(e) => handleStopChange(index, 'address', e.target.value)}
              required={index === 0 && required}
              placeholder={`${label} address...`}
              style={inputStyle}
            />
          </div>

          {/* Time Window */}
          <div style={{ marginBottom: '0.75rem' }}>
            <div style={{ marginBottom: '0.5rem' }}>
              <label style={{ 
                display: 'block', 
                color: 'var(--text-muted)', 
                marginBottom: '0.25rem',
                fontSize: '0.8rem'
              }}>
                Earliest Time
              </label>
              <DateTimePicker
                value={stop.time_window_start}
                onChange={(value) => handleStopChange(index, 'time_window_start', value)}
              />
            </div>
            <div>
              <label style={{ 
                display: 'block', 
                color: 'var(--text-muted)', 
                marginBottom: '0.25rem',
                fontSize: '0.8rem'
              }}>
                Latest Time
              </label>
              <DateTimePicker
                value={stop.time_window_end}
                onChange={(value) => handleStopChange(index, 'time_window_end', value)}
              />
            </div>
          </div>

          {/* Stop Notes */}
          <div>
            <label style={{ 
              display: 'block', 
              color: 'var(--text-muted)', 
              marginBottom: '0.25rem',
              fontSize: '0.8rem'
            }}>
              Stop Notes (optional)
            </label>
            <input
              type="text"
              value={stop.notes}
              onChange={(e) => handleStopChange(index, 'notes', e.target.value)}
              placeholder="Gate code, contact person, etc."
              style={{
                ...inputStyle,
                border: '1px solid var(--border-default)'
              }}
            />
          </div>
        </div>
      ))}

      {stops.length >= MAX_STOPS && (
        <p style={{ 
          color: 'var(--color-warning)', 
          fontSize: '0.85rem',
          margin: '0.5rem 0 0 0'
        }}>
          Maximum of {MAX_STOPS} {label.toLowerCase()} stops reached
        </p>
      )}
    </div>
  );
}

export default MultiStopInput;
